import { useEffect, useRef, useState } from "react";
import { Star, Camera, Loader2, BadgeCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImageUrl } from "../../lib/api";

function Stars({ value, onChange, size = "w-5 h-5" }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type={onChange ? "button" : undefined}
          onClick={onChange ? () => onChange(n) : undefined}
          disabled={!onChange}
          data-testid={onChange ? `star-${n}` : undefined}
          className={onChange ? "cursor-pointer" : "cursor-default"}
          aria-label={`${n} estrelas`}
        >
          <Star className={`${size} ${n <= value ? "fill-[var(--brand)] text-[var(--brand)]" : "text-[#D5CDC2]"}`} />
        </button>
      ))}
    </div>
  );
}

export default function ReviewsSection({ products = [] }) {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ avg: 0, count: 0 });
  const [form, setForm] = useState({ customer_name: "", rating: 5, comment: "", customer_email: "", product_id: "", website: "" });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/reviews").then((res) => setReviews(res.data)).catch(() => {});
    api.get("/reviews/summary").then((res) => setSummary(res.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const photos = [];
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        const { data } = await api.post("/review-photo", fd);
        photos.push(data.url);
      }
      const { data } = await api.post("/reviews", { ...form, photos });
      toast.success(data.message);
      setSent(true);
      setForm({ customer_name: "", rating: 5, comment: "", customer_email: "", product_id: "", website: "" });
      setPhoto(null);
      setPhotoPreview("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não consegui enviar. Tente novamente.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="avaliacoes" className="py-24 px-6 max-w-6xl mx-auto border-t border-[#E8E2D9]">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--brand)] block mb-2">Comunidade de Clientes</span>
          <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight">Depoimentos & Experiências</h2>
        </div>
        {summary.count > 0 && (
          <div className="flex items-center gap-3" data-testid="reviews-summary">
            <Stars value={Math.round(summary.avg)} />
            <span className="font-display font-bold text-2xl">{String(summary.avg).replace(".", ",")}</span>
            <span className="text-sm text-[#6B645C]">({summary.count} {summary.count === 1 ? "avaliação" : "avaliações"})</span>
          </div>
        )}
      </div>

      {reviews.length === 0 ? (
        <div className="p-8 rounded-3xl bg-[var(--site-bg)] border border-dashed border-[#D5CDC2] text-center mb-12" data-testid="reviews-empty">
          <p className="text-sm text-[#6B645C]">Ainda não há avaliações publicadas. Seja o primeiro a contar sua experiência!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-testid="reviews-list">
          {reviews.map((r) => (
            <article key={r.id} className="bg-white rounded-2xl border border-[#E8E2D9] p-6 flex flex-col" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-3">
                <Stars value={r.rating} size="w-4 h-4" />
                {r.verified && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-full">
                    <BadgeCheck className="w-3 h-3" /> Compra verificada
                  </span>
                )}
              </div>
              <p className="text-sm text-[#1A1816] leading-relaxed flex-1">“{r.comment}”</p>
              {r.photos?.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {r.photos.map((p, i) => (
                    <img key={i} src={resolveImageUrl(p)} alt={`Foto da avaliação de ${r.customer_name}`} className="w-16 h-16 rounded-xl object-cover border border-[#E8E2D9]" loading="lazy" />
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#F2EDE4]">
                <span className="font-display font-semibold text-sm">{r.customer_name}</span>
                <span className="font-mono text-[11px] text-[#6B645C]">{new Date(r.created_at).toLocaleDateString("pt-BR")}</span>
              </div>
              {r.reply && (
                <div className="mt-3 bg-[#F2EDE4] rounded-xl px-4 py-3" data-testid={`review-reply-${r.id}`}>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-[var(--brand)] mb-1">Resposta da Dra.Cell</p>
                  <p className="text-xs text-[#5A534C] leading-relaxed">{r.reply}</p>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="max-w-xl mx-auto bg-[#F2EDE4] rounded-3xl border border-[#E8E2D9] p-8" data-testid="review-form-card">
        <h3 className="font-display font-bold text-xl mb-1">Avalie sua experiência</h3>
        <p className="text-sm text-[#5A534C] mb-6">Comprou ou consertou seu aparelho com a gente? Conte como foi. Sua avaliação passa pela loja antes de aparecer no site.</p>
        {sent ? (
          <div className="text-center py-6" data-testid="review-sent-message">
            <MessageSquare className="w-10 h-10 text-[var(--brand)] mx-auto mb-3" />
            <p className="font-display font-semibold">Obrigado pela avaliação!</p>
            <p className="text-sm text-[#6B645C] mt-1">Ela aparecerá aqui assim que a loja aprovar.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {/* honeypot invisível anti-spam */}
            <input type="text" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs uppercase tracking-wider text-[#5A534C]">Sua nota:</span>
              <Stars value={form.rating} onChange={(n) => setForm((f) => ({ ...f, rating: n }))} />
            </div>
            <input required minLength={2} maxLength={80} value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))}
              placeholder="Seu nome" data-testid="review-name-input"
              className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
            <textarea required minLength={3} maxLength={1000} rows={3} value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
              placeholder="Conte como foi o produto, serviço ou atendimento..." data-testid="review-comment-input"
              className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors resize-none" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input type="email" value={form.customer_email} onChange={(e) => setForm((f) => ({ ...f, customer_email: e.target.value }))}
                placeholder="E-mail da compra (opcional)" data-testid="review-email-input"
                className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
              <select value={form.product_id} onChange={(e) => setForm((f) => ({ ...f, product_id: e.target.value }))} data-testid="review-product-select"
                className="w-full bg-white border border-[#E8E2D9] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors">
                <option value="">Avaliação geral da loja</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <input ref={fileRef} type="file" accept="image/*" onChange={pickPhoto} className="hidden" data-testid="review-photo-input" />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="review-photo-button"
                className="inline-flex items-center gap-2 border border-[#D5CDC2] px-4 py-2.5 rounded-full font-display text-xs uppercase tracking-wider font-medium hover:border-[#1A1816] transition-colors">
                <Camera className="w-4 h-4" /> {photo ? "Trocar foto" : "Anexar foto (opcional)"}
              </button>
              {photoPreview && <img src={photoPreview} alt="Prévia da foto" className="w-12 h-12 rounded-xl object-cover border border-[#E8E2D9]" />}
            </div>
            <button type="submit" disabled={sending} data-testid="review-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1816] text-white py-3.5 rounded-full font-display text-sm font-semibold hover:bg-[var(--brand)] transition-colors disabled:opacity-60">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              {sending ? "Enviando..." : "Enviar Avaliação"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
